import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart2, Check, Star, Repeat, Search, Eye, Download, FileText, ArrowRight, TrendingUp } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import useAppStore from '../../store/useAppStore';
import { ReportStatus, ReportType, Report } from '../../types';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid, AreaChart, Area } from 'recharts';
import { isWithinInterval, format } from 'date-fns';
import { arSA } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

const AnimatedNumber: React.FC<{ value: number; isPercentage?: boolean }> = ({ value, isPercentage = false }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        let start = 0;
        const end = value;
        if (start === end) {
            setDisplayValue(end);
            return;
        }
        const duration = 1200;
        let startTime: number | null = null;
        const step = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            const currentVal = easedProgress * (end - start) + start;
            setDisplayValue(isPercentage ? parseFloat(currentVal.toFixed(1)) : Math.floor(currentVal));
            if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [value, isPercentage]);

    return <span>{displayValue}{isPercentage ? '%' : ''}</span>;
};

const statusVariant: { [key in ReportStatus]: string } = {
    [ReportStatus.Approved]: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    [ReportStatus.Pending]: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    [ReportStatus.Rejected]: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const statusLabel: { [key in ReportStatus]: string } = {
    [ReportStatus.Approved]: 'مقبول',
    [ReportStatus.Pending]: 'قيد الانتظار',
    [ReportStatus.Rejected]: 'مرفوض',
};

const ModernEmployeeAnalyticsScreen: React.FC = () => {
    const { t, user } = useAppContext();
    const { reports } = useAppStore();
    const navigate = useNavigate();
    const [tableSearchTerm, setTableSearchTerm] = useState('');
    const [filteredReports, setFilteredReports] = useState<Report[]>([]);

    const employeeReports = useMemo(() => {
        if (!user) return [];
        return reports.filter(r => r.employeeId === user.employeeId);
    }, [reports, user]);

    const stats = useMemo(() => {
        const total = employeeReports.length;
        const approved = employeeReports.filter(r => r.status === ReportStatus.Approved).length;
        const approvalRate = total > 0 ? (approved / total) * 100 : 0;
        const evaluated = employeeReports.filter(r => r.evaluation);
        const avgRating = evaluated.length > 0 ? evaluated.reduce((sum, r) => sum + (r.evaluation?.rating || 0), 0) / evaluated.length : 0;
        const workflows = 0; // Placeholder for workflow count

        return {
            total,
            approvalRate,
            avgRating,
            workflows,
        };
    }, [employeeReports]);

    const kpiCards = [
        { title: 'إجمالي التقارير', value: stats.total, icon: FileText, isPercentage: false, color: 'text-blue-400', bg: 'bg-blue-500/10' },
        { title: 'نسبة القبول', value: stats.approvalRate, icon: Check, isPercentage: true, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
        { title: 'متوسط التقييم', value: stats.avgRating, icon: Star, isPercentage: false, color: 'text-amber-400', bg: 'bg-amber-500/10' },
        { title: 'طلبات سير العمل', value: stats.workflows, icon: Repeat, isPercentage: false, color: 'text-purple-400', bg: 'bg-purple-500/10' },
    ];

    const reportTypeDistribution = useMemo(() => {
        const distribution = employeeReports.reduce((acc, report) => {
            acc[report.type] = (acc[report.type] || 0) + 1;
            return acc;
        }, {} as { [key in ReportType]?: number });
        return Object.entries(distribution).map(([name, value]) => ({ name, value }));
    }, [employeeReports]);

    const COLORS: { [key: string]: string } = {
        [ReportType.Sales]: '#3b82f6',
        [ReportType.Maintenance]: '#10b981',
        [ReportType.Project]: '#f97316',
        [ReportType.Inquiry]: '#64748b'
    };

    const monthlyPerformance = useMemo(() => {
        const now = new Date();
        const data = [];
        for (let i = 5; i >= 0; i--) {
            const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const end = new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59, 999);
            const monthName = format(start, 'MMM', { locale: arSA });

            const count = employeeReports.filter(r => isWithinInterval(new Date(r.date), { start, end })).length;
            data.push({ name: monthName, reports: count });
        }
        return data;
    }, [employeeReports]);

    useEffect(() => {
        const lowerCaseSearch = tableSearchTerm.toLowerCase();
        const filtered = employeeReports.filter(report =>
            report.id.toLowerCase().includes(lowerCaseSearch) ||
            report.type.toLowerCase().includes(lowerCaseSearch) ||
            report.status.toLowerCase().includes(lowerCaseSearch)
        );
        setFilteredReports(filtered);
    }, [tableSearchTerm, employeeReports]);

    const handlePieClick = (data: any) => {
        if (data && data.name) {
            setTableSearchTerm(data.name);
        }
    };

    const handleExport = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredReports);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'MyReports');
        XLSX.writeFile(workbook, `MyReports-${user?.name}.xlsx`);
        toast.success('تم تصدير البيانات بنجاح!');
    };

    if (!user) return null;

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-2">{t('analytics')}</h1>
                    <p className="text-slate-400">تحليل الأداء والتقارير</p>
                </div>
                <button
                    onClick={() => navigate('/')}
                    className="px-4 py-2 rounded-xl glass-card hover:bg-white/10 text-slate-300 transition-all flex items-center gap-2"
                >
                    <ArrowRight size={18} />
                    <span>رجوع للرئيسية</span>
                </button>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {kpiCards.map(({ title, value, icon: Icon, isPercentage, color, bg }) => (
                    <div key={title} className="glass-card p-6 rounded-2xl flex flex-col items-center justify-center text-center group hover:bg-white/5 transition-all">
                        <div className={`p-3 rounded-xl ${bg} ${color} mb-3 group-hover:scale-110 transition-transform`}>
                            <Icon size={24} />
                        </div>
                        <p className="text-3xl font-bold text-white mb-1">
                            <AnimatedNumber value={value} isPercentage={isPercentage} />
                        </p>
                        <p className="text-sm text-slate-400">{title}</p>
                    </div>
                ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-2 glass-card p-6 rounded-2xl">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <PieChart size={20} className="text-blue-400" />
                        توزيع التقارير
                    </h3>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={reportTypeDistribution}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    label={({ name, percent }: any) => (percent != null ? `${(percent * 100).toFixed(0)}%` : '')}
                                    onClick={handlePieClick}
                                    className="cursor-pointer"
                                >
                                    {reportTypeDistribution.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[entry.name]} stroke="rgba(0,0,0,0)" />)}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }}
                                    itemStyle={{ color: '#fff' }}
                                />
                                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="lg:col-span-3 glass-card p-6 rounded-2xl">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <TrendingUp size={20} className="text-purple-400" />
                        أداؤك (آخر 6 أشهر)
                    </h3>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={monthlyPerformance}>
                                <defs>
                                    <linearGradient id="colorReports" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255, 255, 255, 0.05)" />
                                <XAxis dataKey="name" stroke="#94a3b8" tick={{ fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                <Tooltip
                                    contentStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }}
                                    itemStyle={{ color: '#fff' }}
                                />
                                <Area type="monotone" dataKey="reports" name="التقارير" stroke="#8b5cf6" strokeWidth={3} fillOpacity={1} fill="url(#colorReports)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Reports Table */}
            <div className="glass-card p-6 rounded-2xl">
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <FileText size={20} className="text-emerald-400" />
                        سجل تقاريري الشامل
                    </h3>
                    <div className="flex items-center gap-3 w-full md:w-auto">
                        <div className="relative flex-1 md:w-64">
                            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                            <input
                                type="text"
                                value={tableSearchTerm}
                                onChange={e => setTableSearchTerm(e.target.value)}
                                placeholder="بحث..."
                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 pr-10 pl-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>
                        <button
                            onClick={handleExport}
                            className="px-4 py-2.5 rounded-xl bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 border border-slate-700/50 transition-colors flex items-center gap-2"
                        >
                            <Download size={18} />
                            <span className="hidden md:inline">تصدير</span>
                        </button>
                    </div>
                </div>

                {filteredReports.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-slate-300">
                            <thead className="bg-slate-800/50 text-slate-400">
                                <tr>
                                    <th className="p-3 text-right rounded-tr-xl">المعرف</th>
                                    <th className="p-3 text-right">النوع</th>
                                    <th className="p-3 text-right">التاريخ</th>
                                    <th className="p-3 text-right">الحالة</th>
                                    <th className="p-3 text-right rounded-tl-xl">الإجراء</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {filteredReports.map(report => (
                                    <tr key={report.id} className="hover:bg-white/5 transition-colors">
                                        <td className="p-3 font-mono text-slate-400">{report.id}</td>
                                        <td className="p-3">{report.type}</td>
                                        <td className="p-3">{new Date(report.date).toLocaleDateString('ar-SA')}</td>
                                        <td className="p-3">
                                            <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${statusVariant[report.status]}`}>
                                                {statusLabel[report.status]}
                                            </span>
                                        </td>
                                        <td className="p-3">
                                            <button
                                                onClick={() => navigate(`/reports/${report.id}`)}
                                                className="p-2 rounded-lg text-blue-400 hover:bg-blue-500/10 transition-colors flex items-center gap-1"
                                            >
                                                <Eye size={16} />
                                                <span>عرض</span>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                        <FileText size={48} className="mb-4 opacity-20" />
                        <p>لا توجد تقارير مطابقة لبحثك.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ModernEmployeeAnalyticsScreen;
